import {
  cssColorToHex6,
  parseCssColor,
  serializeCssColor,
} from './cssColorWithAlpha';

/** Detect CSS solid colors / gradients usable as carousel slide backgrounds. */
export function isCssBackgroundValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return true;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(v)) return true;
  if (/^(linear|radial|conic)-gradient\(/i.test(v)) return true;
  return false;
}

/**
 * Image / media URL backgrounds (http(s), data:image, local media paths).
 * When both an image URL and a CSS value could apply, image URLs win for Browse
 * and typed media paths; CSS wins only for explicit #hex / gradient strings.
 */
export function isImageBackgroundValue(value: string): boolean {
  const v = value.trim();
  if (!v || isCssBackgroundValue(v)) return false;
  return (
    /^https?:\/\//i.test(v) ||
    v.startsWith('data:image') ||
    v.startsWith('/api/saas/v1/media/') ||
    (v.startsWith('/api/') && /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(v))
  );
}

/** First line of a carousel slide block that should be treated as media/background (not legacy text). */
export function isCarouselSlideMedia(first: string): boolean {
  const v = first.trim();
  if (!v) return false;
  return isImageBackgroundValue(v) || isCssBackgroundValue(v);
}

export type SlideBgMode = 'solid' | 'gradient';

export interface SlideGradientStop {
  /** `#rrggbb` (or free-typed CSS) for the stop color. */
  color: string;
  /** 0–1 opacity for this stop (default 1). */
  alpha: number;
  /** 0–100 */
  offset: number;
}

export interface SlideBgDraft {
  mode: SlideBgMode;
  /** Solid RGB as `#rrggbb` for the native color input. */
  solid: string;
  /** 0–1 solid opacity (default 1 = fully opaque). */
  solidAlpha: number;
  angle: number;
  stops: SlideGradientStop[];
}

const DEFAULT_STOPS: SlideGradientStop[] = [
  { color: '#0f766e', alpha: 1, offset: 0 },
  { color: '#1e293b', alpha: 1, offset: 100 },
];

function clampOffset(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** Normalize a solid color token to `#rrggbb` for inputs; keep unknown CSS as-is. */
function normalizeSolidToken(raw: string): { hex: string; alpha: number } {
  const parsed = parseCssColor(raw);
  if (parsed) {
    return { hex: parsed.hex, alpha: parsed.alpha };
  }
  const v = raw.trim();
  return { hex: cssColorToHex6(v, '#0f766e'), alpha: 1 };
}

function stopCss(color: string, alpha: number): string {
  const parsed = parseCssColor(color);
  if (parsed) return serializeCssColor(parsed.hex, alpha);
  // Free-typed non-hex (e.g. named color) — only attach alpha via rgba when we can parse.
  return color.trim() || '#0f766e';
}

/** Parse a stored slide background into an editable draft (best-effort). */
export function parseSlideBackground(value: string): SlideBgDraft {
  const v = value.trim();
  if (!v) {
    // Match empty swatch preview (#e2e8f0) — never seed another field’s teal/navy default.
    return {
      mode: 'solid',
      solid: '#e2e8f0',
      solidAlpha: 1,
      angle: 135,
      stops: [
        { color: '#e2e8f0', alpha: 1, offset: 0 },
        { color: '#1e293b', alpha: 1, offset: 100 },
      ],
    };
  }

  // Image URLs are not edited as CSS — seed a default draft so Apply can replace them.
  if (isImageBackgroundValue(v)) {
    return {
      mode: 'gradient',
      solid: '#0f766e',
      solidAlpha: 1,
      angle: 135,
      stops: DEFAULT_STOPS.map((s) => ({ ...s })),
    };
  }

  if (
    /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v) ||
    /^(rgb|rgba|hsl|hsla)\(/i.test(v)
  ) {
    const tok = normalizeSolidToken(v);
    return {
      mode: 'solid',
      solid: tok.hex,
      solidAlpha: tok.alpha,
      angle: 135,
      stops: [
        { color: tok.hex, alpha: tok.alpha, offset: 0 },
        { color: '#1e293b', alpha: 1, offset: 100 },
      ],
    };
  }

  const linear = v.match(/^linear-gradient\(\s*([\d.]+)deg\s*,(.+)\)$/i);
  if (linear) {
    const angle = Math.round(Number(linear[1])) || 135;
    const stops = parseGradientStops(linear[2]);
    if (stops.length >= 2) {
      return {
        mode: 'gradient',
        solid: stops[0].color,
        solidAlpha: stops[0].alpha,
        angle,
        stops,
      };
    }
  }

  return {
    mode: 'gradient',
    solid: '#0f766e',
    solidAlpha: 1,
    angle: 135,
    stops: DEFAULT_STOPS.map((s) => ({ ...s })),
  };
}

function parseGradientStops(body: string): SlideGradientStop[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of body) {
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    if (ch === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());

  const stops: SlideGradientStop[] = [];
  for (const part of parts) {
    const m = part.match(/^(.+?)\s+(\d+(?:\.\d+)?)%\s*$/);
    if (m) {
      const tok = normalizeSolidToken(m[1]);
      stops.push({ color: tok.hex, alpha: tok.alpha, offset: clampOffset(Number(m[2])) });
    } else if (part) {
      const tok = normalizeSolidToken(part);
      stops.push({
        color: tok.hex,
        alpha: tok.alpha,
        offset: stops.length === 0 ? 0 : 100,
      });
    }
  }
  if (stops.length === 1) {
    stops.push({ color: '#1e293b', alpha: 1, offset: 100 });
  }
  return stops.slice(0, 3);
}

/** Serialize draft to CSS solid (`#rrggbb` / `rgba(...)`) or linear-gradient(...). */
export function serializeSlideBackground(draft: SlideBgDraft): string {
  if (draft.mode === 'solid') {
    return serializeCssColor(draft.solid, draft.solidAlpha ?? 1);
  }
  const stops = [...draft.stops]
    .slice(0, 3)
    .map((s) => ({
      color: stopCss(s.color, s.alpha ?? 1),
      offset: clampOffset(s.offset),
    }))
    .sort((a, b) => a.offset - b.offset);
  if (stops.length < 2) {
    return `linear-gradient(${draft.angle}deg, ${DEFAULT_STOPS[0].color} 0%, ${DEFAULT_STOPS[1].color} 100%)`;
  }
  const stopList = stops.map((s) => `${s.color} ${s.offset}%`).join(', ');
  return `linear-gradient(${Math.round(draft.angle)}deg, ${stopList})`;
}

/** Checkerboard so translucent paints are visible in swatches / live preview. */
const CHECKERBOARD = 'repeating-conic-gradient(#c4c4cc 0% 25%, #f0f0f4 0% 50%)';

/** Inline style for a preview swatch (CSS bg, or image URL / data URL). */
export function slideBackgroundPreviewStyle(value: string): Record<string, string> {
  const v = value.trim();
  if (!v) {
    return { background: '#e2e8f0' };
  }
  // CSS solids / gradients (#hex, rgba, linear-gradient). Layer checkerboard under translucent paints.
  // Do not also set background shorthand alone — that would clear backgroundImage layers.
  if (isCssBackgroundValue(v)) {
    const paintLayer = /^(linear|radial|conic)-gradient\(/i.test(v)
      ? v
      : `linear-gradient(${v}, ${v})`;
    return {
      backgroundImage: `${paintLayer}, ${CHECKERBOARD}`,
      backgroundSize: 'auto, 8px 8px',
      backgroundColor: 'transparent',
    };
  }
  // http(s) / media paths render as images (Browse + typed URLs).
  return {
    backgroundImage: `url("${v.replace(/"/g, '\\"')}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: '#e2e8f0',
  };
}
